import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock3, RefreshCcw, Timer } from "lucide-react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { getTestSessions } from "@/lib/api";

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat("tr-TR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
};

const formatDuration = (totalSeconds) => {
  const safeSeconds = Math.max(0, Number(totalSeconds || 0));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

export default function AdminTestHistory() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadSessions = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getTestSessions();
      setSessions(data);
    } catch {
      setError("Test geçmişi yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  return (
    <div className="min-h-screen bg-paper bg-grain">
      <Header />
      <main className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 pt-10 pb-20">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 text-sm text-[#5c635e] hover:text-[#1c2723] mb-3"
            >
              <ArrowLeft size={14} /> Yönetim paneli
            </Link>
            <h1 className="font-serif-fact text-3xl text-[#1c2723] flex items-center gap-3">
              <Timer size={24} className="text-[#2e4733]" />
              Test Geçmişi
            </h1>
            <p className="text-sm text-[#5c635e] mt-2">
              Kullanıcıların tamamladığı testlerin süre, skor ve kategori kayıtları.
            </p>
          </div>
          <Button
            type="button"
            onClick={loadSessions}
            className="rounded-full bg-[#2e4733] hover:bg-[#1c2723]"
          >
            <RefreshCcw size={14} className="mr-2" />
            Yenile
          </Button>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-[#e8eae3] bg-white p-8 text-[#5c635e]">
            Yükleniyor...
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-red-800">
            {error}
          </div>
        ) : sessions.length === 0 ? (
          <div className="rounded-2xl border border-[#e8eae3] bg-white p-8 text-[#5c635e]">
            Henüz kaydedilmiş test yok.
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-[#e8eae3] bg-white">
            <div className="grid grid-cols-[1.3fr_1.4fr_0.7fr_0.7fr_0.8fr_0.9fr] gap-3 bg-[#f5f6f2] px-4 py-3 label-eyebrow text-[#5c635e]">
              <div>Kullanıcı</div>
              <div>Kategori</div>
              <div>Skor</div>
              <div>Soru</div>
              <div>Süre</div>
              <div>Tarih</div>
            </div>
            <div className="divide-y divide-[#e8eae3]">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  className="grid grid-cols-[1.3fr_1.4fr_0.7fr_0.7fr_0.8fr_0.9fr] gap-3 px-4 py-3 text-sm text-[#1c2723]"
                >
                  <div>
                    <div className="font-medium">{session.user_name || session.username}</div>
                    <div className="label-eyebrow text-[#a3aba6]">@{session.username}</div>
                  </div>
                  <div>
                    <div className="font-medium">{session.category_name}</div>
                    <div className="label-eyebrow text-[#a3aba6]">
                      {session.mode === "review" ? "Tekrar modu" : "Normal test"}
                      {session.ended_early ? " · erken bitirildi" : ""}
                    </div>
                  </div>
                  <div className="font-serif-fact text-xl">%{session.score_pct}</div>
                  <div>
                    {session.answered_count}/{session.question_count}
                    <div className="text-xs text-[#5c635e]">
                      {session.correct_count} doğru · {session.wrong_count} yanlış
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-2">
                    <Clock3 size={14} className="text-[#2e4733]" />
                    {formatDuration(session.duration_seconds)}
                  </div>
                  <div>{formatDateTime(session.created_at)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
